import type { Ref } from 'vue';
import { ref } from 'vue';

/** How long an optimistic change may wait for the CQRS query-side to catch up. */
export const PENDING_TTL_MS = 120_000;

export interface PendingEntry<V> {
	value: V;
	addedAt: number;
}

interface ReconcileOptions {
	/** Entries for which it returns false never expire (e.g. temporary ids). */
	canExpire?: (id: string) => boolean;
}

export interface PendingEntries<V> {
	entries: Ref<Map<string, PendingEntry<V>>>;
	get: (id: string) => V | undefined;
	/** Present and younger than PENDING_TTL_MS. */
	isFresh: (id: string) => boolean;
	values: () => V[];
	/** Stores the value (refreshing its age) and returns a rollback to the previous entry. */
	set: (id: string, value: V) => () => void;
	remove: (id: string) => void;
	/** Moves an entry to a new id, keeping its position and refreshing its age. */
	rename: (fromId: string, toId: string, mapValue?: (value: V) => V) => void;
	/** Drops the expired entries and those the query-side now reflects. */
	reconcile: (
		isSettled: (id: string, value: V) => boolean,
		options?: ReconcileOptions
	) => void;
}

/**
 * Optimistic changes keyed by entity id, kept until the query-side reflects them
 * or PENDING_TTL_MS has elapsed. Each change replaces the map so readers react.
 */
export const createPendingEntries = <V>(): PendingEntries<V> => {
	const entries = ref(new Map()) as Ref<Map<string, PendingEntry<V>>>;

	const isExpired = (entry: PendingEntry<V>) =>
		Date.now() - entry.addedAt > PENDING_TTL_MS;

	const get = (id: string) => entries.value.get(id)?.value;

	const isFresh = (id: string) => {
		const entry = entries.value.get(id);
		return entry !== undefined && !isExpired(entry);
	};

	const values = () => [...entries.value.values()].map((entry) => entry.value);

	const set = (id: string, value: V) => {
		const previous = entries.value.get(id);
		const next = new Map(entries.value);
		next.set(id, { value, addedAt: Date.now() });
		entries.value = next;

		return () => {
			const restored = new Map(entries.value);
			if (previous) restored.set(id, previous);
			else restored.delete(id);
			entries.value = restored;
		};
	};

	const remove = (id: string) => {
		if (!entries.value.has(id)) return;
		const next = new Map(entries.value);
		next.delete(id);
		entries.value = next;
	};

	const rename = (
		fromId: string,
		toId: string,
		mapValue: (value: V) => V = (value) => value
	) => {
		if (!entries.value.has(fromId)) return;
		entries.value = new Map(
			[...entries.value].map(([id, entry]) =>
				id === fromId
					? [toId, { value: mapValue(entry.value), addedAt: Date.now() }]
					: [id, entry]
			)
		);
	};

	const reconcile = (
		isSettled: (id: string, value: V) => boolean,
		{ canExpire = () => true }: ReconcileOptions = {}
	) => {
		if (entries.value.size === 0) return;
		const remaining = new Map(
			[...entries.value].filter(
				([id, entry]) =>
					!(canExpire(id) && isExpired(entry)) && !isSettled(id, entry.value)
			)
		);
		if (remaining.size !== entries.value.size) entries.value = remaining;
	};

	return { entries, get, isFresh, values, set, remove, rename, reconcile };
};
